interface Props {
  docId?: string;
  canPublish: boolean;
  engineStatus: 'idle' | 'stale' | 'current';
  engineHash?: string;
  engineConfidence?: string;
  isRunning?: boolean;
  runningLabel?: string;
  onRunReport: () => void;
  onViewPortal: () => void;
  onPrintReport: () => void;
  canPrintReport: boolean;
}

/**
 * Get build version info from Vite env vars
 */
function getBuildInfo() {
  const gitCommit = import.meta.env.VITE_GIT_COMMIT || 'unknown';
  const buildTime = import.meta.env.VITE_BUILD_TIME || 'unknown';

  // Format: short SHA + readable time
  const shortSha = gitCommit.slice(0, 7);
  let formattedTime = 'unknown';

  if (buildTime !== 'unknown') {
    try {
      const date = new Date(buildTime);
      formattedTime = date.toISOString().slice(0, 16).replace('T', ' ');
    } catch {
      formattedTime = buildTime;
    }
  }

  return `build: ${shortSha} ${formattedTime}`;
}

/**
 * Check if dev mode is active
 */
function isDevModeActive(): boolean {
  if (typeof window === 'undefined') return false;
  const devParam = new URLSearchParams(window.location.search).get('dev');
  return devParam === '1';
}

export function TopStrip({
  docId,
  canPublish,
  engineStatus,
  engineHash,
  engineConfidence,
  isRunning = false,
  runningLabel,
  onRunReport,
  onViewPortal,
  onPrintReport,
  canPrintReport,
}: Props) {
  const dotClass = engineStatus === 'current'
    ? ''
    : engineStatus === 'stale'
      ? ' hub-engine-dot--stale'
      : ' hub-engine-dot--none';

  const statusLabel = engineStatus === 'current'
    ? 'Report generated and ready'
    : engineStatus === 'stale'
      ? 'Data changed — click Run report to update'
      : 'Complete assessment and select an option to generate report';

  const buildInfo = getBuildInfo();
  const devMode = isDevModeActive();

  const isDisabled = !canPublish || isRunning;
  const btnClass = isRunning
    ? 'hub-strip-btn hub-strip-btn--primary hub-strip-btn--running'
    : `hub-strip-btn hub-strip-btn--primary${!canPublish ? ' hub-strip-btn--disabled' : ''}`;

  return (
    <div className="hub-top-strip">
      <div className="hub-top-strip__left">
        <h1 className="hub-top-strip__title">System Assessment</h1>
        {docId && <span className="hub-top-strip__doc-id">{docId}</span>}
        <span className="hub-top-strip__build-info">{buildInfo}</span>
      </div>

      <div className="hub-top-strip__right">
        {devMode && process.env.NODE_ENV !== 'production' && (
          <div className="hub-dev-badge">
            DEV=1
          </div>
        )}

        <div className="hub-engine-status">
          <span className={`hub-engine-dot${dotClass}`} />
          <span>{statusLabel}</span>
          {engineHash && <span style={{ fontFamily: 'monospace' }}>{engineHash.slice(0, 8)}</span>}
          {engineConfidence && <span>({engineConfidence})</span>}
        </div>

        <button
          className={btnClass}
          onClick={onRunReport}
          disabled={isDisabled}
          title={!canPublish ? 'Select a system option first' : 'Save assessment and generate report'}
        >
          {isRunning && <span className="hub-strip-btn__spinner" />}
          {isRunning ? (runningLabel ?? 'Running...') : 'Run report'}
        </button>

        <button 
          className={`hub-strip-btn${isRunning ? ' hub-strip-btn--disabled' : ''}`}
          onClick={onViewPortal}
          disabled={isRunning}
          title={docId ? 'Open customer portal in new tab' : 'Save assessment first to view portal'}
        >
          View portal
        </button>

        <button
          className={`hub-strip-btn${!canPrintReport ? ' hub-strip-btn--disabled' : ''}`}
          onClick={onPrintReport}
          disabled={!canPrintReport}
          title={!canPrintReport ? 'Select at least one option to enable print' : undefined}
        >
          Print PDF
        </button>
      </div>
    </div>
  );
}
